import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { LoggingService } from '../logging.service';
import { NotificationService } from '../notification.service';
import { ProfileService } from './profile.service';
import { Profile } from '../shared/models/profile.model';

interface DialogData {
  profile: Profile,
}

interface Avatars {
  custom?: string,
  gravatar?: string,
  provider?: string,
}

interface AvatarsPayload {
  avatar: string,
  avatars: Avatars,
}

enum AvatarOption {
  Custom = 'custom',
  Gravatar = 'gravatar',
  Provider = 'provider',
}

interface AvatarOptions {
  custom: AvatarOption,
  gravatar?: AvatarOption,
  provider?: AvatarOption,
}

@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'avatar-dialog',
  },
  styleUrls: ['./avatar-dialog.component.scss'],
  templateUrl: './avatar-dialog.component.html',
})
export class AvatarDialogComponent {
  public avatar = ''
  public avatars: Avatars = {}
  public avatarOptions: AvatarOptions = {
    custom: AvatarOption.Custom,
    gravatar: AvatarOption.Gravatar,
    provider: AvatarOption.Provider,
  }
  public customAvatarField: FormControl
  public selectedOption: AvatarOption
  public useBlankAvatar = true

  @ViewChild('customAvatar', { static: false }) customAvatar: ElementRef
  @ViewChild('providerAvatar', { static: false }) providerAvatar: ElementRef

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private formBuilder: FormBuilder,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    public dialogRef: MatDialogRef<AvatarDialogComponent>,
  ) { }

  ngOnInit(): void {
    this.avatar = this.data.profile.avatar;
    this.avatars = this.data.profile.avatars;
    this.selectedOption = this.avatar === this.avatars.custom
      ? AvatarOption.Custom
      : this.avatar === this.avatars.gravatar
        ? AvatarOption.Gravatar
        : AvatarOption.Provider;
    this.customAvatarField = this.formBuilder.control(
      this.avatars.custom || '',
      [Validators.required, Validators.pattern(/^https?:\/\/[^\s$.?#].[^\s]*$/)],
    );
    this.customAvatarField.valueChanges.subscribe(() => this.useBlankAvatar = this.customAvatarField.invalid);
    this.customAvatarField[this.selectedOption !== AvatarOption.Custom ? 'disable' : 'enable']();
  }

  onImageSrcError(): void {
    this.useBlankAvatar = true;
    this.customAvatarField.setErrors({ 'imageNotFound': true });
  }

  onGravatarSrcError(): void {
    this.avatar = this.avatars.provider;
    delete this.avatars.gravatar;
  }

  selectAvatar(event: Event): void {
    this.customAvatar.nativeElement.classList.remove('selected');
    this.providerAvatar?.nativeElement.classList.remove('selected');
    const currentTarget = event.currentTarget as HTMLElement;
    currentTarget.classList.add('selected');
    this.selectedOption = currentTarget.getAttribute('data-value') as AvatarOption;
    this.customAvatarField[this.selectedOption !== AvatarOption.Custom ? 'disable' : 'enable']();
  }

  cancelAvatar(): void {
    this.dialogRef.close();
  }

  saveAvatar(): void {
    const avatar = this.selectedOption === AvatarOption.Custom
      ? this.customAvatarField.value
      : this.selectedOption === AvatarOption.Gravatar
        ? this.avatars.gravatar
        : this.avatars.provider;
    if (Object.keys(this.avatars).length === 0) {
      this.avatars.custom = avatar;
    }
    const payload: AvatarsPayload = { avatar, avatars: this.avatars };
    this.profileService.putProfile(payload).subscribe(
      () => {
        this.notifications.success('profile.changeAvatar.success');
        this.dialogRef.close();
      },
      error => {
        this.notifications.error('profile.changeAvatar.error');
        this.logger.error(error);
      },
    );
  }
}
